<template>
  <div class="add-item-out-container">
    <ds-page-header :title="$t('addItemOut.page.title')" :subtitle="$t('addItemOut.page.subtitle')">
      <template #extra>
        <el-button size="small" icon="el-icon-back" @click="goBack">{{ $t('addItemOut.return') }}</el-button>
        <el-button type="primary" size="small" icon="el-icon-check" @click="_finishStep">{{ $t('addItemOut.submit') }}</el-button>
      </template>
    </ds-page-header>

    <div class="ds-panel ds-panel--padded">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('addItemOut.recipient') }}</span>
      </div>
      <el-form :model="addItemOutInfo" label-width="120px">
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('addItemOut.warehouse')">
              <el-select v-model="addItemOutInfo.shId" @change="_computeFlow" style="width:100%" clearable
                :placeholder="$t('addItemOut.warehousePlaceholder')">
                <el-option v-for="(item, index) in addItemOutInfo.storehouses" :key="index" :label="item.shName" :value="item.shId" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('addItemOut.recipient')">
              <el-input :placeholder="$t('addItemOut.contactPlaceholder')" v-model.trim="addItemOutInfo.endUserInfo.staffName" clearable style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('addItemOut.contactPhone')">
              <el-input :placeholder="$t('addItemOut.phonePlaceholder')" v-model.trim="addItemOutInfo.endUserInfo.staffTel" clearable style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :span="24" v-if="addItemOutInfo.canChooseEndUserOrNot">
            <el-form-item :label="$t('addItemOut.selectRecipient')">
              <el-button type="primary" size="small" @click="chooseEndUser"><i class="el-icon-search"></i> {{ $t('addItemOut.selectRecipient') }}</el-button>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('addItemOut.applicationNote')">
              <el-input type="textarea" :rows="2" :placeholder="$t('addItemOut.notePlaceholder')" v-model.trim="addItemOutInfo.description" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <div class="ds-panel">
      <ds-toolbar :show-column-setting="false" :show-density="false" :show-refresh="false">
        <template #left><span class="ds-panel__title">{{ $t('addItemOut.itemInfo') }}</span></template>
        <template #right>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="_openSelectResourceStoreInfoModel">{{ $t('addItemOut.selectItem') }}</el-button>
        </template>
      </ds-toolbar>
      <ds-data-table :data="addItemOutInfo.resourceStores" border :empty-description="$t('addItemOut.page.empty')">
        <el-table-column :label="$t('addItemOut.itemType')" align="center" min-width="140">
          <template slot-scope="scope">{{ scope.row.parentRstName || '-' }} &gt; {{ scope.row.rstName || '-' }}</template>
        </el-table-column>
        <el-table-column :label="$t('addItemOut.itemName')" align="center" min-width="160">
          <template slot-scope="scope">{{ scope.row.resName }}({{ scope.row.resCode }})</template>
        </el-table-column>
        <el-table-column :label="$t('addItemOut.itemSpec')" align="center" width="110">
          <template slot-scope="scope">{{ scope.row.rssName || '-' }}</template>
        </el-table-column>
        <el-table-column :label="$t('addItemOut.price')" align="center" min-width="140">
          <template slot-scope="scope">
            <el-select v-model="scope.row.timesId" @change="_changeTimesId($event, scope.$index)" style="width:100%">
              <el-option value="" :label="$t('addItemOut.selectPrice')" />
              <el-option v-for="time in scope.row.times" :key="time.timesId" :value="time.timesId" :label="String(time.price)" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column :label="$t('addItemOut.stock')" align="center" width="110">
          <template slot-scope="scope"><span class="ds-num">{{ _getTimesStock(scope.row) }}{{ scope.row.unitCodeName }}</span></template>
        </el-table-column>
        <el-table-column :label="$t('addItemOut.applyQuantity')" align="center" min-width="160">
          <template slot-scope="scope">
            <el-input v-model.trim="scope.row.quantity" type="number" :placeholder="$t('addItemOut.quantityPlaceholder')" style="width:70%" />
            &nbsp;{{ scope.row.unitCodeName }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('addItemOut.remark')" align="center" min-width="140">
          <template slot-scope="scope"><el-input v-model.trim="scope.row.remark" type="text" :placeholder="$t('addItemOut.remarkPlaceholder')" clearable /></template>
        </el-table-column>
        <el-table-column :label="$t('addItemOut.operation')" align="center" width="90" fixed="right">
          <template slot-scope="scope">
            <el-button type="danger" size="mini" @click="_removeSelectResourceStoreItem(scope.row.resId)">{{ $t('addItemOut.remove') }}</el-button>
          </template>
        </el-table-column>
      </ds-data-table>
    </div>

    <div v-if="addItemOutInfo.audit.assignee === '-2'" class="ds-panel ds-panel--padded">
      <div class="ds-panel__header"><span class="ds-panel__title">{{ $t('addItemOut.approver') }}</span></div>
      <el-form label-width="120px">
        <el-form-item :label="$t('addItemOut.approver')">
          <el-row :gutter="16">
            <el-col :span="17"><el-input :placeholder="$t('addItemOut.approverPlaceholder')" v-model="addItemOutInfo.audit.staffName" disabled /></el-col>
            <el-col :span="6"><el-button type="primary" @click="chooseStaff"><i class="el-icon-search"></i> {{ $t('addItemOut.select') }}</el-button></el-col>
          </el-row>
        </el-form-item>
      </el-form>
    </div>

    <choose-resource-store2 ref="chooseResourceStore2" @setSelectResourceStores="handleSetSelectResourceStores" />
    <select-staff ref="selectStaff" @selectStaff="handleStaffChange" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import ChooseResourceStore2 from '@/components/resource/chooseResourceStore2'
import SelectStaff from '@/components/staff/SelectStaff'
import { goodsCollection, listStorehouses, queryFirstAuditStaff } from '@/api/resource/addItemOutApi'
import { getUserName, getUserTel } from '@/api/user/userApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'

export default {
  name: 'AddItemOutList',
  components: { ChooseResourceStore2, SelectStaff, DsPageHeader, DsToolbar, DsDataTable },
  data() {
    return {
      addItemOutInfo: {
        resourceStores: [], resourceSuppliers: [],
        audit: { assignee: '', staffId: '', staffName: '', taskId: '' },
        endUserInfo: { assignee: '', staffId: '', staffName: '', taskId: '', staffTel: '' },
        description: '', endUserName: '', endUserTel: '', acceptStaffId: '', file: '',
        resOrderType: '20000', staffId: '', staffName: '', communityId: '',
        shId: '', storehouses: [], flowId: '', canChooseEndUserOrNot: false
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.addItemOutInfo.communityId = this.communityId
    this._reset()
    this.initPage()
  },
  methods: {
    _reset() {
      this.addItemOutInfo = {
        resourceStores: [], resourceSuppliers: [],
        audit: { assignee: '', staffId: '', staffName: '', taskId: '' },
        endUserInfo: { assignee: '', staffId: '', staffName: '', taskId: '', staffTel: '' },
        description: '', endUserName: '', endUserTel: '', acceptStaffId: '', file: '',
        resOrderType: '20000', staffId: '', staffName: '', communityId: this.communityId,
        shId: '', storehouses: [], flowId: '', canChooseEndUserOrNot: false
      }
    },
    initPage() {
      const from = this.$route.query.from
      if (from === 'resourceStore') this.addItemOutInfo.canChooseEndUserOrNot = true
      this.addItemOutInfo.endUserInfo.staffName = getUserName() || ''
      this.addItemOutInfo.endUserInfo.staffTel = getUserTel() || ''
      this._loadResourceSuppliers()
      this._listPurchaseStorehouses()
    },
    goBack() { this.$router.go(-1) },
    _resourcesFromSameHouse(resourcesList) {
      if (!resourcesList || resourcesList.length < 2) return true
      let lastHouse = ''
      for (let i = 0; i < resourcesList.length; i++) {
        if (!lastHouse) { lastHouse = resourcesList[i].shId; continue }
        if (lastHouse !== resourcesList[i].shId) return false
      }
      return true
    },
    _openSelectResourceStoreInfoModel() {
      if (!this.addItemOutInfo.shId) { this.$message.error(this.$t('addItemOut.warehouseRequired')); return }
      if (!this.addItemOutInfo.endUserInfo.staffName) { this.$message.error(this.$t('addItemOut.contactRequired')); return }
      if (!this.addItemOutInfo.endUserInfo.staffTel) { this.$message.error(this.$t('addItemOut.phoneRequired')); return }
      if (!this.addItemOutInfo.description) { this.$message.error(this.$t('addItemOut.noteRequired')); return }
      this.$refs.chooseResourceStore2.open({ shId: this.addItemOutInfo.shId })
    },
    async _finishStep() {
      const resourceStores = this.addItemOutInfo.resourceStores
      if (!resourceStores || resourceStores.length === 0) { this.$message.error(this.$t('addItemOut.itemRequired')); return }
      if (!this._resourcesFromSameHouse(resourceStores)) { this.$message.error(this.$t('addItemOut.sameWarehouseRequired')); return }
      this.addItemOutInfo.endUserName = this.addItemOutInfo.endUserInfo.staffName
      this.addItemOutInfo.endUserTel = this.addItemOutInfo.endUserInfo.staffTel
      this.addItemOutInfo.acceptStaffId = this.addItemOutInfo.endUserInfo.staffId
      if (this.addItemOutInfo.canChooseEndUserOrNot) this.addItemOutInfo.useSwitch = 'OFF'
      try {
        const res = await goodsCollection(this.addItemOutInfo)
        if (res.code === 0) { this.$message.success(this.$t('common.operationSuccess')); this.goBack() }
        else this.$message.error(res.msg || this.$t('addItemOut.submitFailed'))
      } catch (error) { this.$message.error(this.$t('addItemOut.submitError')); console.error(error) }
    },
    _loadResourceSuppliers() {},
    async _listPurchaseStorehouses() {
      try {
        const res = await listStorehouses({ page: 1, row: 100, communityId: '', allowPurchase: 'ON' })
        this.addItemOutInfo.storehouses = res.data
      } catch (error) { console.error('请求失败:', error) }
    },
    _removeSelectResourceStoreItem(resId) {
      this.addItemOutInfo.resourceStores = this.addItemOutInfo.resourceStores.filter(item => item.resId !== resId)
    },
    _changeTimesId(value, index) {
      const times = this.addItemOutInfo.resourceStores[index].times
      times.forEach(item => { if (item.timesId === value) this.addItemOutInfo.resourceStores[index].selectedStock = item.stock })
      this.$nextTick(() => { this.$forceUpdate() })
    },
    _getTimesStock(resourceStore) {
      if (!resourceStore.timesId) return '-'
      let stock = 0
      resourceStore.times.forEach(item => { if (item.timesId === resourceStore.timesId) stock = item.stock })
      return stock
    },
    async _loadStaffOrg(flowId) {
      const res = await queryFirstAuditStaff({ communityId: this.communityId, flowId: flowId })
      if (res.code != 0) return
      let _data = res.data
      Object.assign(this.addItemOutInfo.audit, _data[0])
      if (!_data[0].assignee.startsWith('-')) this.addItemOutInfo.audit.staffId = this.addItemOutInfo.audit.assignee
    },
    chooseStaff() { this.$refs.selectStaff.open(this.addItemOutInfo.audit) },
    chooseEndUser() { this.$refs.selectStaff.open(this.addItemOutInfo.endUserInfo) },
    _computeFlow() {
      this.addItemOutInfo.resourceStores = []
      const storehouse = this.addItemOutInfo.storehouses.find(item => item.shId === this.addItemOutInfo.shId)
      if (storehouse) { this.addItemOutInfo.flowId = storehouse.useFlowId; this._loadStaffOrg(storehouse.useFlowId) }
    },
    handleSetSelectResourceStores(resourceStores) {
      const oldList = this.addItemOutInfo.resourceStores
      const newList = [...resourceStores]
      newList.forEach((newItem, newIndex) => {
        newItem.rsId = ''
        newItem.timesId = ''
        if (newItem.times && newItem.times.length > 0) newItem.timesId = newItem.times[0].timesId
        oldList.forEach(oldItem => {
          if (oldItem.resId === newItem.resId && newItem.times && newItem.times.length < 2) delete newList[newIndex]
        })
      })
      const filteredList = newList.filter(item => item['resId'])
      this.addItemOutInfo.resourceStores = [...oldList, ...filteredList]
    },
    handleStaffChange(staff) {
      this.addItemOutInfo.audit.staffId = staff.staffId
      this.addItemOutInfo.audit.staffName = staff.staffName
      this.addItemOutInfo.endUserInfo.staffId = staff.staffId
      this.addItemOutInfo.endUserInfo.staffName = staff.staffName
    }
  }
}
</script>

<style lang="scss" scoped>
.add-item-out-container { padding: var(--ds-spacing-lg); background-color: var(--ds-gray-100); min-height: calc(100vh - 60px); }
.ds-panel { background: #fff; border: 1px solid var(--ds-gray-300); border-radius: var(--ds-radius-md); box-shadow: var(--ds-shadow-sm); overflow: hidden; margin-bottom: var(--ds-spacing-lg);
  &--padded { padding: var(--ds-spacing-lg); }
  &__header { display: flex; align-items: center; justify-content: space-between; padding-bottom: var(--ds-spacing-md); margin-bottom: var(--ds-spacing-md); border-bottom: 1px solid var(--ds-gray-200); }
  &__title { font-size: var(--ds-font-size-lg); font-weight: 600; color: var(--ds-gray-800); }
}
</style>
